import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch, getToken } from './api';
import './AdminExamSchedule.css';

const SLOT_MINUTES = 30;
const DAY_START = 8;
const DAY_END = 19;

const pad = (value) => String(value).padStart(2, '0');
const dateKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};
const dateFromKey = (value) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};
const formatDay = (date) => new Intl.DateTimeFormat('tr-TR', { weekday: 'short', day: 'numeric', month: 'short' }).format(date);
const formatDateTime = (value) => new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' }).format(new Date(value));
const statusLabel = { Draft: 'Taslak', Published: 'Yayında', Paused: 'Duraklatıldı', Completed: 'Tamamlandı', Archived: 'Geçmiş sınav' };
const courseThemes = [
  { background: '#dff3e1', border: '#2f8f3c', text: '#185c1e' },
  { background: '#dff1fb', border: '#1679b7', text: '#075985' },
  { background: '#f3e8ff', border: '#7e4ab3', text: '#5b2686' },
  { background: '#fff0d9', border: '#bd6a11', text: '#8a4300' },
  { background: '#fde7ed', border: '#c23d63', text: '#8e2044' },
  { background: '#e4f5f2', border: '#168178', text: '#0d625b' },
];

const getCourseTheme = (exam) => {
  const key = String(exam.course_id || exam.course_code || exam.course_name || exam.program_name || 'exam');
  const hash = Array.from(key).reduce((value, character) => ((value * 31) + character.charCodeAt(0)) >>> 0, 0);
  return courseThemes[hash % courseThemes.length];
};

const courseThemeStyle = (exam) => {
  const theme = getCourseTheme(exam);
  return { '--course-bg': theme.background, '--course-border': theme.border, '--course-text': theme.text };
};

const scheduleFetch = async (path) => {
  try {
    return await apiFetch(path);
  } catch (error) {
    // Windows'ta bazı yerel proxy/DNS ayarları localhost'u IPv6'ya yönlendirip
    // yalnızca IPv4 dinleyen Flask geliştirme sunucusuna ulaşmayı engelleyebilir.
    if (window.location.hostname !== 'localhost') throw error;
    const token = getToken();
    return fetch(`http://127.0.0.1:5105/api${path}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });
  }
};

const buildScheduleFromRectorDashboard = (data, start, end) => {
  const rangeStart = new Date(`${dateKey(start)}T00:00:00`);
  const rangeEnd = new Date(`${dateKey(addDays(end, 1))}T00:00:00`);
  const exams = (data.faculties || []).flatMap((faculty) =>
    (faculty.programs || []).flatMap((program) =>
      (program.courses || []).flatMap((course) =>
        (course.exams || []).map((exam) => ({
          ...exam,
          course_code: course.course_code,
          course_name: course.course_name,
          program_name: program.program_name || 'Bölüm bilgisi yok',
          faculty_name: faculty.faculty_name || 'Fakülte bilgisi yok',
          instructor_name: 'Akademisyen bilgisi için sınav detayına bakın'
        }))
      )
    )
  ).filter((exam) =>
    ['Draft', 'Published', 'Paused', 'Completed', 'Archived'].includes(exam.status) &&
    new Date(exam.start_at) < rangeEnd && new Date(exam.end_at) >= rangeStart
  );
  const now = new Date();
  return {
    exams,
    summary: {
      total: exams.length,
      live: exams.filter((exam) => new Date(exam.start_at) <= now && now <= new Date(exam.end_at)).length,
      programs: new Set(exams.map((exam) => exam.program_name)).size
    }
  };
};

function AdminExamSchedule() {
  const today = useMemo(() => new Date(), []);
  const [weekStart, setWeekStart] = useState(() => new Date(today.getFullYear(), today.getMonth(), today.getDate() - ((today.getDay() + 6) % 7)));
  const [exams, setExams] = useState([]);
  const [summary, setSummary] = useState({ total: 0, live: 0, programs: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const selectedSlotDetailRef = useRef(null);

  const days = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)), [weekStart]);
  const slots = useMemo(() => Array.from({ length: (DAY_END - DAY_START) * 60 / SLOT_MINUTES }, (_, index) => {
    const totalMinutes = DAY_START * 60 + index * SLOT_MINUTES;
    return `${pad(Math.floor(totalMinutes / 60))}:${pad(totalMinutes % 60)}`;
  }), []);

  useEffect(() => {
    const loadSchedule = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await scheduleFetch(`/admin/exam-schedule?start=${dateKey(days[0])}&end=${dateKey(days[6])}`);
        if (response.ok) {
          const data = await response.json();
          if (!data.success) throw new Error(data.message || 'Takvim yüklenemedi');
          setExams(data.exams || []);
          setSummary(data.summary || { total: 0, live: 0, programs: 0 });
        } else if (response.status === 404) {
          // Eski backend süreçleri yeni takvim rotasını içermeyebilir. Mevcut,
          // admin erişimine açık üniversite raporundan aynı takvim verisini üret.
          const legacyResponse = await scheduleFetch('/rector/dashboard');
          const legacyData = await legacyResponse.json();
          if (!legacyResponse.ok || !legacyData.success) {
            throw new Error(legacyData.message || 'Takvim yüklenemedi');
          }
          const schedule = buildScheduleFromRectorDashboard(legacyData, days[0], days[6]);
          setExams(schedule.exams);
          setSummary(schedule.summary);
        } else {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.message || 'Takvim yüklenemedi');
        }
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setLoading(false);
      }
    };
    loadSchedule();
  }, [days]);

  const findExamsAt = (day, time) => {
    const slotStart = new Date(`${dateKey(day)}T${time}:00`);
    const slotEnd = new Date(slotStart.getTime() + SLOT_MINUTES * 60000);
    return exams.filter((exam) => new Date(exam.start_at) < slotEnd && new Date(exam.end_at) > slotStart);
  };

  const selectedLabel = selectedSlot && `${formatDay(dateFromKey(selectedSlot.date))}, ${selectedSlot.time}`;
  const selectedExams = selectedSlot
    ? findExamsAt(dateFromKey(selectedSlot.date), selectedSlot.time)
    : [];

  useEffect(() => {
    if (!selectedSlot || selectedExams.length === 0) return undefined;
    const scrollFrame = window.requestAnimationFrame(() => {
      selectedSlotDetailRef.current?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start'
      });
    });
    return () => window.cancelAnimationFrame(scrollFrame);
  }, [selectedSlot, selectedExams.length]);

  return (
    <section className="admin-schedule" aria-labelledby="admin-schedule-title">
      <header className="admin-schedule__header">
        <div>
          <p className="admin-schedule__eyebrow">SINAV YÖNETİMİ</p>
          <h2 id="admin-schedule-title">Sınav Takvimi</h2>
          <p>2026–2027 akademik yılındaki geçmiş ve planlanan sınavları bölüm bazında izleyin; boş saatleri güncelleme için seçin.</p>
        </div>
        <div className="admin-schedule__week-actions" aria-label="Hafta kontrolü">
          <button type="button" onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label="Önceki hafta">←</button>
          <button type="button" className="admin-schedule__today" onClick={() => setWeekStart(new Date(today.getFullYear(), today.getMonth(), today.getDate() - ((today.getDay() + 6) % 7)))}>Bu hafta</button>
          <button type="button" onClick={() => setWeekStart(addDays(weekStart, 7))} aria-label="Sonraki hafta">→</button>
        </div>
      </header>

      <div className="admin-schedule__stats" aria-label="Takvim özeti">
        <div><strong>{summary.total}</strong><span>Bu haftaki tüm sınav</span></div>
        <div><strong>{summary.live}</strong><span>Şu an devam eden</span></div>
        <div><strong>{summary.programs}</strong><span>Sınavı olan bölüm</span></div>
      </div>

      <div className="admin-schedule__legend">
        <span><i className="admin-schedule__legend-dot admin-schedule__legend-dot--busy" />Sınav saati (geçmiş dahil)</span>
        <span><i className="admin-schedule__legend-dot admin-schedule__legend-dot--available" />Seçilebilir boş saat</span>
        <span><i className="admin-schedule__legend-dot admin-schedule__legend-dot--course" />Her renk farklı bir ders</span>
        {selectedLabel && <span className="admin-schedule__selection">Seçilen: <strong>{selectedLabel}{selectedExams.length ? ` · ${selectedExams.length} sınav` : ' · Boş saat'}</strong></span>}
      </div>

      {loading ? <div className="admin-schedule__state">Takvim yükleniyor…</div> : error ? <div className="admin-schedule__state admin-schedule__state--error">{error}</div> : (
        <div className="admin-schedule__grid-wrap">
          <div className="admin-schedule__grid" role="grid" aria-label="Haftalık sınav takvimi">
            <div className="admin-schedule__corner">Saat</div>
            {days.map((day) => <div className={`admin-schedule__day ${dateKey(day) === dateKey(today) ? 'is-today' : ''}`} key={dateKey(day)}>{formatDay(day)}</div>)}
            {slots.map((time) => <React.Fragment key={time}>
              <div className="admin-schedule__time">{time}</div>
              {days.map((day) => {
                const slotExams = findExamsAt(day, time);
                const exam = slotExams[0];
                const key = `${dateKey(day)}-${time}`;
                const isSelected = selectedSlot?.date === dateKey(day) && selectedSlot?.time === time;
                return (
                  <button
                    type="button"
                    key={key}
                    className={`admin-schedule__slot ${exam ? 'is-busy' : 'is-available'} ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => setSelectedSlot({ date: dateKey(day), time })}
                    title={exam ? `${slotExams.length} sınavı görüntüle` : `${formatDay(day)} ${time} boş`}
                    aria-label={exam ? `${formatDay(day)} ${time}: ${slotExams.length} sınavı görüntüle` : `${formatDay(day)} ${time}: boş saat, seç`}
                  >
                    {exam && <span className="admin-schedule__exam" style={courseThemeStyle(exam)}>
                      <span className="admin-schedule__course-dots" aria-hidden="true">
                        {slotExams.slice(0, 4).map((slotExam) => <i key={slotExam.id} style={{ backgroundColor: getCourseTheme(slotExam).border }} />)}
                      </span>
                      <b>{exam.title}{slotExams.length > 1 ? ` +${slotExams.length - 1}` : ''}</b>
                      <small>{slotExams.length > 1 ? `${slotExams.length} çakışan sınav · ` : ''}{exam.course_code || exam.course_name}</small>
                    </span>}
                  </button>
                );
              })}
            </React.Fragment>)}
          </div>
        </div>
      )}

      {!loading && !error && selectedSlot && selectedExams.length > 0 && (
        <section ref={selectedSlotDetailRef} className="admin-schedule__slot-detail" aria-live="polite" aria-labelledby="selected-slot-title">
          <div className="admin-schedule__slot-detail-heading">
            <div>
              <p>SEÇİLİ ZAMAN ARALIĞI</p>
              <h3 id="selected-slot-title">{selectedLabel} — {selectedExams.length} sınav</h3>
            </div>
            <button type="button" onClick={() => setSelectedSlot(null)}>Kapat</button>
          </div>
          <div className="admin-schedule__slot-exams">
            {selectedExams.map((exam) => (
              <article key={exam.id} style={courseThemeStyle(exam)}>
                <div>
                  <strong><i className="admin-schedule__course-marker" />{exam.title}</strong>
                  <span>{exam.program_name} · {exam.course_code || exam.course_name}</span>
                  <small>{formatDateTime(exam.start_at)} – {new Intl.DateTimeFormat('tr-TR', { hour: '2-digit', minute: '2-digit' }).format(new Date(exam.end_at))}</small>
                </div>
                <em className={`admin-schedule__status admin-schedule__status--${exam.status.toLowerCase()}`}>{statusLabel[exam.status] || exam.status}</em>
              </article>
            ))}
          </div>
        </section>
      )}

      {!loading && !error && <div className="admin-schedule__list">
        <h3>Bu haftaki sınavlar</h3>
        {exams.length === 0 ? <p>Bu hafta sınav yok. Tüm saatler seçilebilir.</p> : exams.map((exam) => (
          <article key={exam.id} style={courseThemeStyle(exam)}>
            <time>{formatDateTime(exam.start_at)} – {new Intl.DateTimeFormat('tr-TR', { hour: '2-digit', minute: '2-digit' }).format(new Date(exam.end_at))}</time>
            <div><strong><i className="admin-schedule__course-marker" />{exam.title}</strong><span>{exam.program_name} · {exam.course_code || exam.course_name} · {exam.instructor_name}</span></div>
            <em className={`admin-schedule__status admin-schedule__status--${exam.status.toLowerCase()}`}>{statusLabel[exam.status] || exam.status}</em>
          </article>
        ))}
      </div>}
    </section>
  );
}

export default AdminExamSchedule;
